import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApplicationTemplateV3SidebarStatus,
  ApplicationTemplateV3SidebarStatusKind,
  ApplicationTemplateV3SidebarStatusOption,
  ApplicationTemplateV3SidebarStatusRow,
  PrintIcon,
} from '@uni-space/uni-components';
import { KeycloakContext } from '@shared/config/context/KeycloakContext';
import {
  CLOUD_PRINTER_WORKSTATIONS_KEY,
  IS_CLOUD_PRINTER_CONFIGURED,
  bindCloudPrinterWorkstation,
  getCloudPrinterWorkstations,
  unbindCloudPrinterWorkstation,
  workstationDisplayName,
} from '@shared/api/cloudPrinter';
import {
  StoredPrinterSelection,
  clearSelectedPrinter,
  readSelectedPrinter,
  writeSelectedPrinter,
} from '@shared/utils/printerSelection';

/**
 * Строка выбора принтера в подвале sidebar V3. Кликабельна: раскрывает список рабочих мест
 * drop-off-точки оператора и закрепляет выбранное за ним (`POST /bind`). Занятые другими
 * операторами показываются, но выбрать их нельзя. В подвале меню — пункт «снять принтер»
 * (`POST /unbind`), он появляется только когда выбор есть.
 *
 * Обратная связь — только внутри самого виджета (спиннер «Подключение…», зелёная галочка,
 * цвет точки): ни звуков, ни тостов здесь нет по требованию дизайна.
 *
 * Выбор дублируется в localStorage: список приходит асинхронно, а на стендах без гейтвея
 * облачной печати не приходит вовсе — запомненное имя даёт оператору видеть свой принтер
 * сразу. Когда список всё же пришёл, прав он: расхождение чистит запомненное.
 *
 * Размещается в существующем sidebar через PrinterSidebarPortal. Режим раскрытого
 * или свёрнутого меню задаёт портал. Логика перенесена из fast-track-webui.
 */
export default function PrinterSidebarStatus() {
  const { t } = useTranslation();
  const { userInfo } = useContext(KeycloakContext);
  const queryClient = useQueryClient();

  const currentUserId = userInfo?.sub ?? null;
  const [stored, setStored] = useState<StoredPrinterSelection | null>(() => readSelectedPrinter());
  // Какое рабочее место сейчас подключаем — по нему рисуется спиннер в строке списка.
  const [connectingId, setConnectingId] = useState<string | null>(null);

  const {
    data: workstations,
    isPending: isQueryPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: CLOUD_PRINTER_WORKSTATIONS_KEY,
    queryFn: ({ signal }) => getCloudPrinterWorkstations({ signal }),
    // Гейтвея в этом окружении нет — запрос ушёл бы на сам фронт и вернул index.html.
    enabled: IS_CLOUD_PRINTER_CONFIGURED,
    // Список меняется редко (десктоп регистрируется при старте) — лишний раз не дёргаем,
    // актуализируем принудительно при раскрытии меню.
    staleTime: 60_000,
  });

  // Отключённый запрос у react-query навсегда остаётся в состоянии pending — «загрузку»
  // в этом случае не показываем.
  const isPending = IS_CLOUD_PRINTER_CONFIGURED && isQueryPending;
  // Форму ответа проверяет getCloudPrinterWorkstations, но список приходит и из кэша —
  // страховка от чужой записи в тот же ключ.
  const list = useMemo(() => (Array.isArray(workstations) ? workstations : []), [workstations]);

  const mine = useMemo(
    () => (currentUserId ? list.find((w) => w.boundUserId === currentUserId) : undefined),
    [list, currentUserId],
  );

  // Список пришёл и привязки в нём нет — значит запомненный выбор устарел (сняли с другого
  // устройства, рабочее место удалили). Бэк авторитетнее, чистим локальную запись.
  useEffect(() => {
    if (!IS_CLOUD_PRINTER_CONFIGURED || isPending || isError) return;
    if (mine) {
      const fresh = { id: mine.id, name: workstationDisplayName(mine) };
      if (stored?.id !== fresh.id || stored?.name !== fresh.name) {
        writeSelectedPrinter(fresh);
        setStored(fresh);
      }
    } else if (stored) {
      clearSelectedPrinter();
      setStored(null);
    }
  }, [isPending, isError, mine, stored]);

  const persist = (selection: StoredPrinterSelection | null) => {
    if (selection) {
      writeSelectedPrinter(selection);
      setStored(selection);
    } else {
      clearSelectedPrinter();
      setStored(null);
    }
  };

  const { mutate: bind } = useMutation({
    mutationKey: ['cloudPrinterBindWorkstation'],
    mutationFn: (workstationId: string) => bindCloudPrinterWorkstation(workstationId),
    onSuccess: (status, workstationId) => {
      // Ручка отвечает 200 и на отказах — решает поле status, а не HTTP-код.
      if (status !== 'Success') {
        void queryClient.invalidateQueries({ queryKey: CLOUD_PRINTER_WORKSTATIONS_KEY });
        return;
      }
      const picked = list.find((w) => w.id === workstationId);
      persist({
        id: workstationId,
        name: picked ? workstationDisplayName(picked) : workstationId.slice(0, 8),
      });
      void queryClient.invalidateQueries({ queryKey: CLOUD_PRINTER_WORKSTATIONS_KEY });
    },
    onSettled: () => setConnectingId(null),
  });

  const { mutate: unbind, isPending: isUnbinding } = useMutation({
    mutationKey: ['cloudPrinterUnbindWorkstation'],
    mutationFn: () => unbindCloudPrinterWorkstation(),
    onSuccess: (status) => {
      if (status !== 'Success') return;
      // Локальную запись снимаем только после подтверждения бэка — иначе на экране
      // «не выбран», а задания печати продолжали бы уходить на привязанное рабочее место.
      persist(null);
      void queryClient.invalidateQueries({ queryKey: CLOUD_PRINTER_WORKSTATIONS_KEY });
    },
  });

  const isBusy = connectingId !== null || isUnbinding;
  // Индикатор «идёт запрос»: спиннер вместо точки статуса, пока тянем список
  // рабочих мест или подключаемся к выбранному.
  const showSpinner = isBusy || isPending;

  const row: ApplicationTemplateV3SidebarStatusRow = useMemo(() => {
    // Выбрать можно свободные и своё же (повторный bind идемпотентен).
    const selectableCount = list.filter((w) => w.boundUserId === null || w.boundUserId === currentUserId).length;
    // Пока список не пришёл (или гейтвей недоступен) — показываем запомненный выбор.
    const shownSelection = mine ? { id: mine.id, name: workstationDisplayName(mine) } : stored;

    const options: ApplicationTemplateV3SidebarStatusOption[] = list.map((w) => {
      const takenByOther = w.boundUserId !== null && w.boundUserId !== currentUserId;
      const isSelected = w.id === shownSelection?.id;
      const isConnecting = w.id === connectingId;
      return {
        id: w.id,
        label: workstationDisplayName(w),
        hint: isConnecting
          ? t('printer.connecting')
          : takenByOther
            ? t('printer.takenByOther')
            : isSelected
              ? t('printer.hintMine')
              : t('printer.hintFree'),
        hintTone: isConnecting ? 'busy' : isSelected ? 'ok' : 'muted',
        tone: takenByOther ? 'warn' : 'ok',
        disabled: takenByOther || isBusy,
        busy: isConnecting,
      };
    });

    let status: ApplicationTemplateV3SidebarStatusKind;
    let value: string;
    if (!IS_CLOUD_PRINTER_CONFIGURED) {
      status = 'warn';
      value = t('printer.notConfigured');
    } else if (connectingId) {
      status = 'idle';
      value = t('printer.connecting');
    } else if (mine || (shownSelection && (isPending || isError))) {
      status = mine ? 'ok' : 'warn';
      value = shownSelection!.name;
    } else if (isPending) {
      status = 'idle';
      value = t('printer.checking');
    } else if (isError) {
      status = 'err';
      value = t('printer.loadFailed');
    } else if (selectableCount === 0) {
      // Ни одного свободного: либо список пуст, либо все заняты другими операторами.
      status = 'err';
      value = t('printer.none');
    } else {
      status = 'warn';
      value = t('printer.notSelected');
    }

    return {
      icon: <PrintIcon size="xs" />,
      label: t('printer.label'),
      value,
      status,
      busy: showSpinner,
      options,
      menuTitle: t('printer.menuTitle'),
      selectedId: shownSelection?.id ?? null,
      emptyLabel: IS_CLOUD_PRINTER_CONFIGURED ? t('printer.none') : t('printer.notConfigured'),
      loadingLabel: t('printer.checkingLong'),
      loading: isPending,
      onSelect: (id) => {
        // Повторный выбор того же принтера — ничего не меняет, не дёргаем бэк.
        if (id === shownSelection?.id || isBusy) return;
        setConnectingId(id);
        bind(id);
      },
      // Снять принтер можно, только когда выбор есть — библиотека сама прячет пункт при null.
      onClear: IS_CLOUD_PRINTER_CONFIGURED ? () => unbind() : undefined,
      clearLabel: t('printer.clear'),
      // Меню открыли — список рабочих мест мог измениться (десктоп перезапустили).
      onOpen: IS_CLOUD_PRINTER_CONFIGURED ? () => void refetch() : undefined,
    };
  }, [list, currentUserId, mine, stored, connectingId, isPending, isError, isBusy, showSpinner, t, bind, unbind, refetch]);

  return <ApplicationTemplateV3SidebarStatus rows={[row]} />;
}
